// Whether the live map / crew-tracking features are available for a
// briefing. ONE LOCK BECAME TWO (2026-08-28):
//
//   Lock A — hasStageMapAccess: the map itself, stage zones, your own
//            position, the offline map pack. Available from LITE: a LITE
//            producer drew those zones and must be able to navigate them.
//   Lock B — hasLiveMapAccess: LIVE POSITIONS OF OTHER CREW. PRO/STUDIO
//            only, and enforced server-side too: the RLS on
//            crew_locations (migration 20260828100000) hides other
//            people's rows when this rule says no. The TS rule decides
//            what to RENDER; the database decides what you can READ.
//
// Every consumer gates on these two shared rules — an ungated affordance
// would dead-end crew on the lock screen, and a *looser* rule on one
// surface would show a map the other surface hides. Both locks share one
// entitlement core (grant/active/trial/paid-period); they differ only in
// which tiers qualify.
//
// WHY THIS IS MORE THAN "tier + status"
// -------------------------------------
// The producer's own Settings page reads `check-subscription`, which
// *computes* entitlement (lifetime-free code, comp grant, trial, Stripe)
// and never writes the outcome back to producer_subscriptions. The crew
// app reads the raw row. When the two disagree the producer sees "STUDIO"
// while their whole crew silently loses live tracking.
//
// That is not hypothetical: a lifetime-free code creates a REAL Stripe
// subscription (100%-off forever coupon, no payment method on file). If
// Stripe ever deletes it, stripe-webhook writes plan_status='cancelled'
// and the crew is locked out even though the producer is entitled
// forever. Observed on staging 2026-08-03.
//
// So this rule mirrors check-subscription's notion of entitlement, plus
// one addition it doesn't need: a cancelled plan whose paid period has
// not run out yet still has access until subscription_end.
//
// TWO INPUT SHAPES
// ----------------
// Crew and co-producers read get_briefing_producer_subscription, which
// resolves the grants to booleans server-side — they must not receive
// their producer's billing dates. A producer looking at their OWN
// production passes the check-subscription response instead, which carries
// the raw fields. Both shapes are accepted; each raw field is only a
// fallback for the boolean that replaces it.

export type ProducerSubscriptionLike =
  | {
      plan_tier?: string | null;
      plan_status?: string | null;
      /** profiles.manual_producer_access — access granted by hand. */
      manual_producer_access?: boolean | null;

      // ── resolved server-side (crew / co-producer path) ──
      /** Lifetime-free, manual, or a running comp grant: ignores plan_status. */
      has_standing_grant?: boolean | null;
      /** subscription_end is still in the future. */
      within_paid_period?: boolean | null;
      /** plan_status is 'trial' and it hasn't lapsed. */
      within_trial?: boolean | null;

      // ── raw equivalents (producer's own check-subscription response) ──
      /** monthly | yearly | lifetime_free | comp | manual */
      plan_type?: string | null;
      /** End of the paid/comp period. null = no end (never expires). */
      subscription_end?: string | null;
      trial_ends_at?: string | null;
    }
  | null
  | undefined;

/** Lock B: live positions of other crew. */
const LIVE_CREW_TIERS = ["pro", "studio"];
/** Lock A: the map, zones and own position — every paid tier. */
const STAGE_MAP_TIERS = ["lite", "pro", "studio"];

/** Tier granted when entitlement comes from a grant rather than a paid plan. */
const GRANT_TIER = "studio";

function isFuture(timestamp: string | null | undefined, now: Date): boolean {
  if (!timestamp) return false;
  const parsed = Date.parse(timestamp);
  return Number.isFinite(parsed) && parsed > now.getTime();
}

/**
 * A server-resolved boolean wins outright — including when it is `false`.
 * The raw fields are only consulted when the server didn't answer at all
 * (the producer's own check-subscription response). Falling back on a
 * `false` would resurrect a lapsed trial: crew never receive
 * trial_ends_at, so the raw branch would see "status=trial, no end date"
 * and read that as an open-ended trial.
 */
function resolved(flag: boolean | null | undefined, fromRawFields: () => boolean): boolean {
  return typeof flag === "boolean" ? flag : fromRawFields();
}

/**
 * The shared entitlement core: is this subscription alive at all, and at
 * which tier? Both locks consume this; they differ only in the tier set.
 */
function entitlement(
  sub: NonNullable<ProducerSubscriptionLike>,
  now: Date,
): { entitled: boolean; tier: string } {
  // Entitlement that ignores plan_status entirely, so it outlives a
  // cancelled Stripe subscription (see the header). Mirrors the branches
  // check-subscription evaluates before it ever looks at Stripe.
  const grant = resolved(
    sub.has_standing_grant,
    () =>
      sub.plan_type === "lifetime_free" ||
      sub.manual_producer_access === true ||
      // comp = time-boxed free access, no payment method. null end = unbounded.
      (sub.plan_type === "comp" && (!sub.subscription_end || isFuture(sub.subscription_end, now))),
  );

  // A grant carries no purchased tier of its own, so it resolves to STUDIO
  // — the same fallback check-subscription reports to the producer
  // (`dbSub.plan_tier || 'studio'`). Without it a lifetime account with an
  // empty plan_tier shows STUDIO to the producer and a lock to their crew.
  const tier = sub.plan_tier || (grant ? GRANT_TIER : "");

  const entitled =
    grant ||
    sub.plan_status === "active" ||
    // An open-ended trial row stays trusted — the trial reminder/expiry
    // jobs own flipping it to 'expired'.
    resolved(
      sub.within_trial,
      () => sub.plan_status === "trial" && (!sub.trial_ends_at || isFuture(sub.trial_ends_at, now)),
    ) ||
    // Cancelled/expired/inactive but still inside the period that was paid
    // for: access runs to the end of that period, not to the cancel click.
    resolved(sub.within_paid_period, () => isFuture(sub.subscription_end, now));

  return { entitled, tier };
}

/**
 * Lock B — may this user see live positions of OTHER crew? Mirrored by the
 * SQL predicate briefing_has_live_crew_access (RLS on crew_locations); if
 * this rule and that function ever disagree, the map shows empty instead
 * of erroring, but keep them in step.
 *
 * @param now injectable for tests; defaults to the current time.
 */
export function hasLiveMapAccess(sub: ProducerSubscriptionLike, now: Date = new Date()): boolean {
  if (!sub) return false;
  const { entitled, tier } = entitlement(sub, now);
  return entitled && LIVE_CREW_TIERS.includes(tier);
}

/**
 * Lock A — may this user see the map at all: stage zones, own position,
 * offline pack. Everything a LITE producer drew themselves.
 */
export function hasStageMapAccess(sub: ProducerSubscriptionLike, now: Date = new Date()): boolean {
  if (!sub) return false;
  const { entitled, tier } = entitlement(sub, now);
  return entitled && STAGE_MAP_TIERS.includes(tier);
}

/**
 * What a live-map surface should render.
 *
 * `unavailable` exists so an outage stops masquerading as "your producer
 * has no subscription" — that message sends crew chasing a producer whose
 * plan is fine. Cached data still wins over an error: a failed background
 * refetch must not tear down a working map.
 */
export type LiveMapGate = "loading" | "unavailable" | "locked" | "ready";

interface GateQuery {
  isLoading: boolean;
  isError: boolean;
  subscription: ProducerSubscriptionLike;
}

function resolveGate(
  query: GateQuery,
  rule: (sub: ProducerSubscriptionLike, now: Date) => boolean,
  now: Date,
): LiveMapGate {
  if (query.isLoading) return "loading";
  if (rule(query.subscription, now)) return "ready";
  if (query.isError && !query.subscription) return "unavailable";
  return "locked";
}

/**
 * The one sentence both crew maps show when Lock B is closed while the
 * map itself is open. Shared so the two platforms cannot drift — this
 * exact feature's history is copy drifting between surfaces.
 */
export const LIVE_CREW_LOCK_NOTICE = "Live crew positions require a PRO or STUDIO plan";

/** Lock B as a render state: live positions of other crew. */
export function resolveLiveMapGate(query: GateQuery, now: Date = new Date()): LiveMapGate {
  return resolveGate(query, hasLiveMapAccess, now);
}

/** Lock A as a render state: the map surface itself. */
export function resolveStageMapGate(query: GateQuery, now: Date = new Date()): LiveMapGate {
  return resolveGate(query, hasStageMapAccess, now);
}
